import { useState } from "react";
import { Cloud, Rocket, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const GRADIENT =
  "linear-gradient(135deg, rgba(134,87,241,0.18) 0%, rgba(107,63,214,0.08) 100%)";

// Mirrors the backend's CF subdomain validator: lowercase alphanumerics
// + internal hyphens, 1–32 chars, no leading/trailing hyphen. Cheap
// regex feedback so obviously-bad input doesn't round-trip.
const SUBDOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;

interface DeployConfirmData {
  app_name: string;
  app_type: string;
  provider: string;
  estimated_cost: string;
  repo: string;
  branch: string;
  pending_key: string;
  deployment_domain?: string;
  suggested_subdomain?: string | null;
  // Cloudflare Platform enum value-string. Frontend uses this to
  // tweak labels (Pages vs Container) but doesn't gate any UI on it.
  platform?: string;
}

interface DeployConfirmWidgetProps {
  data: DeployConfirmData;
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
}

export function DeployConfirmWidget({ data, onAction }: DeployConfirmWidgetProps) {
  const deploymentDomain = data.deployment_domain ?? "daydra.app";
  const initialSubdomain = data.suggested_subdomain ?? "";
  const [subdomain, setSubdomain] = useState<string>(initialSubdomain);

  const subdomainValid = !subdomain || SUBDOMAIN_RE.test(subdomain);
  const fullDomain = subdomain ? `${subdomain}.${deploymentDomain}` : "";

  const handleDeploy = () => {
    const payload: Record<string, unknown> = {
      pending_key: data.pending_key,
    };
    if (subdomain && subdomainValid) {
      payload.subdomain = subdomain;
    }
    onAction?.("confirm_deploy", {
      __action: "confirm_deploy",
      __dismiss: true,
      __payload: payload,
    });
  };

  const handleCancel = () => {
    onAction?.("cancel_deploy", {
      __action: "cancel_deploy",
      __dismiss: true,
      __payload: { pending_key: data.pending_key },
    });
  };

  return (
    <Card
      variant="default"
      padding="default"
      className="max-w-sm animate-scale-in"
    >
      <div
        className="rounded-t-xl -mx-4 -mt-4 px-4 pt-4 pb-3 mb-3 overflow-hidden"
        style={{ background: GRADIENT }}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-accent/15">
            <Cloud size={16} className="text-daydra-accent" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-daydra-text truncate">
              Deploy {data.app_name}
            </h3>
            <p className="text-2xs text-daydra-muted truncate">
              {data.app_type} · {data.provider}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2.5 mb-4 text-xs">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-daydra-muted">Repo</span>
          <span className="text-daydra-text truncate font-mono">
            {data.repo}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-daydra-muted">Branch</span>
          <span className="text-daydra-text font-mono">{data.branch}</span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-daydra-muted">Cost</span>
          <span className="text-daydra-text">{data.estimated_cost}</span>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-2xs text-daydra-muted mb-1">
          Subdomain
        </label>
        <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] focus-within:border-daydra-accent/40 transition-colors">
          <input
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value.trim().toLowerCase())}
            className="flex-1 min-w-0 bg-transparent text-sm text-daydra-text font-mono placeholder:text-daydra-muted/50 focus:outline-none"
            placeholder="my-app"
            spellCheck={false}
            autoComplete="off"
          />
          <span className="text-2xs text-daydra-muted shrink-0 font-mono">
            .{deploymentDomain}
          </span>
        </div>
        {fullDomain && subdomainValid && (
          <p className="text-2xs text-daydra-muted/70 mt-1 font-mono truncate">
            → {fullDomain}
          </p>
        )}
        {subdomain && !subdomainValid && (
          <p className="text-2xs text-amber-400 mt-1">
            Lowercase letters, numbers, and hyphens only. Can't start or end
            with a hyphen.
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="flex-1"
          onClick={handleCancel}
        >
          <X size={14} className="mr-1.5" />
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          className="flex-1"
          onClick={handleDeploy}
          disabled={!subdomainValid}
        >
          <Rocket size={14} className="mr-1.5" />
          Deploy
        </Button>
      </div>
    </Card>
  );
}
